import { useCallback, useEffect, useState } from "react";
import { RefreshCw, UserPlus } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { CopyButton, OnboardingArtistTable, OnboardingSummary, referralLink } from "../../components/OnboardingReport";

const EMPTY_FORM = { first_name: "", last_name: "", email: "", phone: "", password: "" };

export default function AdminOnboardingManagers({ toast }) {
  const [managers, setManagers] = useState([]);
  const [unassigned, setUnassigned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [artistQuery, setArtistQuery] = useState("");
  const [artistResults, setArtistResults] = useState([]);
  const [assignTo, setAssignTo] = useState({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/onboarding-managers");
      setManagers(response.data.managers || []);
      setUnassigned(response.data.unassigned_artists || 0);
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);

  const loadReport = useCallback(async (managerId) => {
    setSelectedId(managerId);
    setReportLoading(true);
    try {
      const response = await api.get(`/admin/onboarding-managers/${managerId}/report`);
      setReport(response.data);
    } catch (error) {
      toast(formatApiError(error), "error");
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  }, [toast]);

  // Artist search for assigning / reassigning.
  useEffect(() => {
    const q = artistQuery.trim();
    if (q.length < 2) { setArtistResults([]); return undefined; }
    const timer = setTimeout(() => {
      api.get("/admin/onboarding-managers/artists/search", { params: { q } })
        .then((response) => setArtistResults(response.data || []))
        .catch(() => setArtistResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [artistQuery]);

  const createManager = async (event) => {
    event.preventDefault();
    setCreating(true);
    try {
      const response = await api.post("/admin/onboarding-managers", form);
      toast(`Created ${response.data.name} — referral code ${response.data.referral_code}`, "success");
      setForm(EMPTY_FORM);
      setShowCreate(false);
      reload();
    } catch (error) {
      toast(formatApiError(error), "error");
    } finally {
      setCreating(false);
    }
  };

  const setActive = async (manager, active) => {
    try {
      await api.patch(`/admin/onboarding-managers/${manager.id}`, { active });
      toast(active ? `${manager.name} reactivated` : `${manager.name} deactivated — login and referral code disabled`, "success");
      reload();
    } catch (error) {
      toast(formatApiError(error), "error");
    }
  };

  const assign = async (artist, managerId) => {
    try {
      await api.put(`/admin/artists/${artist.artist_id}/onboarding-manager`, { manager_id: managerId || null });
      const manager = managers.find((m) => m.id === managerId);
      toast(managerId ? `${artist.stage_name} assigned to ${manager?.name || "manager"}` : `${artist.stage_name} unassigned`, "success");
      setArtistResults((rows) => rows.map((row) => row.artist_id === artist.artist_id
        ? { ...row, manager_id: managerId || null, manager_name: manager?.name || null }
        : row));
      reload();
      if (selectedId) loadReport(selectedId);
    } catch (error) {
      toast(formatApiError(error), "error");
    }
  };

  const activeManagers = managers.filter((m) => m.active);

  return (
    <div className="grid gap-16" data-testid="admin-onboarding-managers">
      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Onboarding Managers ({managers.length})</div>
            <div className="text-muted fs-11">
              Each manager has a referral code. Artists who sign up with it (link or typed code), or who you assign below, appear in that manager's report.
              {unassigned > 0 && ` ${unassigned} artists have no manager.`}
            </div>
          </div>
          <div className="flex gap-8">
            <button type="button" className="btn btn-gold btn-sm" onClick={() => setShowCreate((v) => !v)} data-testid="obm-add">
              <UserPlus size={14} aria-hidden="true" /> Add manager
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={reload} disabled={loading}>
              <RefreshCw size={14} aria-hidden="true" /> {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {showCreate && (
          <form className="card-pad" onSubmit={createManager} data-testid="obm-create-form" style={{ borderBottom: "1px solid var(--bg4)" }}>
            <div className="grid grid-2 gap-12">
              {[
                ["first_name", "First name", "text", true],
                ["last_name", "Last name", "text", false],
                ["email", "Email (their login)", "email", true],
                ["phone", "Phone", "tel", false],
                ["password", "Temporary password (min 8)", "text", true],
              ].map(([key, label, type, required]) => (
                <div key={key} className="field" style={{ marginBottom: 0 }}>
                  <div className="field-label">{label}{required && <span className="text-red"> *</span>}</div>
                  <input
                    className="field-input"
                    type={type}
                    required={required}
                    minLength={key === "password" ? 8 : undefined}
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    data-testid={`obm-create-${key}`}
                  />
                </div>
              ))}
            </div>
            <div className="text-muted fs-11 mt-8">Share the email and temporary password with the manager. A referral code is generated automatically.</div>
            <div className="flex gap-8 mt-12">
              <button type="submit" className="btn btn-gold btn-sm" disabled={creating} data-testid="obm-create-submit">{creating ? "Creating…" : "Create manager"}</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </form>
        )}

        <div className="table-responsive">
          <table className="table" data-testid="obm-table">
            <thead>
              <tr>
                <th>Manager</th>
                <th>Referral</th>
                <th>Artists</th>
                <th>KYC submitted</th>
                <th>Live</th>
                <th>Avg. questionnaire</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {managers.length === 0 && (
                <tr><td colSpan={8} className="text-muted">{loading ? "Loading…" : "No onboarding managers yet. Add one to generate a referral code."}</td></tr>
              )}
              {managers.map((manager) => (
                <tr key={manager.id} className={selectedId === manager.id ? "active" : ""} data-testid={`obm-row-${manager.id}`}>
                  <td>
                    <div className="fw-600">{manager.name}</div>
                    <div className="text-muted fs-11">{[manager.email, manager.phone].filter(Boolean).join(" · ")}</div>
                  </td>
                  <td>
                    <div className="fw-700" style={{ fontFamily: "ui-monospace, monospace" }}>{manager.referral_code}</div>
                    <CopyButton text={referralLink(manager.referral_code)} label="Copy link" testId={`obm-copy-${manager.id}`} />
                  </td>
                  <td>{manager.summary.artists}</td>
                  <td>{manager.summary.kyc_submitted}</td>
                  <td>{manager.summary.live}</td>
                  <td>{manager.summary.avg_questionnaire_percent}%</td>
                  <td>
                    <span className={`pill ${manager.active ? "pill-green" : "pill-red"}`}>{manager.active ? "Active" : "Deactivated"}</span>
                  </td>
                  <td>
                    <div className="flex gap-8">
                      <button type="button" className="btn btn-gold btn-xs" onClick={() => loadReport(manager.id)} data-testid={`obm-view-${manager.id}`}>View report</button>
                      <button type="button" className="btn btn-ghost btn-xs" onClick={() => setActive(manager, !manager.active)}>
                        {manager.active ? "Deactivate" : "Reactivate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedId && (
        <div className="card card-pad" data-testid="obm-report">
          <div className="flex-between mb-12" style={{ flexWrap: "wrap", gap: 8 }}>
            <div>
              <div className="card-title">{report?.manager?.name || "Manager"} — onboarding report</div>
              {report?.manager && (
                <div className="text-muted fs-11">Code {report.manager.referral_code} · {referralLink(report.manager.referral_code)}</div>
              )}
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSelectedId(null); setReport(null); }}>Close</button>
          </div>
          {reportLoading && !report ? <div className="text-muted">Loading report…</div> : (
            <>
              <OnboardingSummary summary={report?.summary} />
              <OnboardingArtistTable artists={report?.artists} emptyText="No artists linked to this manager yet." />
            </>
          )}
        </div>
      )}

      <div className="card card-pad" data-testid="obm-assign">
        <div className="card-title mb-4">Assign artists to a manager</div>
        <div className="text-muted fs-11 mb-12">Use this for artists who signed up without a code, or to correct a link. Changes are recorded in the audit log.</div>
        <input
          className="field-input"
          placeholder="Search artists by stage name, email or phone…"
          value={artistQuery}
          onChange={(e) => setArtistQuery(e.target.value)}
          data-testid="obm-artist-search"
        />
        {artistResults.length > 0 && (
          <div className="table-responsive mt-12">
            <table className="table">
              <thead><tr><th>Artist</th><th>Current manager</th><th>Assign to</th><th /></tr></thead>
              <tbody>
                {artistResults.map((artist) => {
                  const choice = assignTo[artist.artist_id] ?? artist.manager_id ?? "";
                  return (
                    <tr key={artist.artist_id}>
                      <td><div className="fw-600">{artist.stage_name}</div><div className="text-muted fs-11">{artist.city}</div></td>
                      <td>{artist.manager_name || <span className="text-muted">None</span>}</td>
                      <td>
                        <select className="field-input" value={choice} onChange={(e) => setAssignTo((m) => ({ ...m, [artist.artist_id]: e.target.value }))}>
                          <option value="">No manager</option>
                          {activeManagers.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.referral_code})</option>)}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-gold btn-xs"
                          disabled={choice === (artist.manager_id || "")}
                          onClick={() => assign(artist, choice)}
                          data-testid={`obm-assign-${artist.artist_id}`}
                        >Save</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
